import _ from 'lodash';
import classNames from 'classnames';
import React, { Component, PropTypes } from 'react';
import { bylineToString, getLocalLink } from 'utils/NewsUtils';
import { filterItems } from 'utils/CategoryUtils';
import { INSET_STYLE, INSET_STYLE_VALUE_MAPPING } from '../../constants/FRNBaseStyles';
import Timestamp from 'components/Timestamp';
import {withResources} from 'lib/redux';
import Image from 'components/Image';
import { COLOR_STYLE } from '../../constants/FRNBaseStyles';
import ReadMoreButton from 'components/shared/ReadMoreButton';
import ComponentTitle from 'components/shared/ComponentTitle';
import { showWindow, getDataOfWindowOpen } from 'utils/WindowUtils';

/* Breakpoints: XS: 0-480, S: 481-767, the Rest: 768 -  */
const IMAGE_BREAKPOINTS = [
  { "maxWidth": 480, "widthPX": 133 },
  { "maxWidth": 767, "widthPX": 155 },
  {                  "widthPX": 316 } // Max width of the image is always equal or less than 316px
];

const CardListItemProps = {
  widthStyle: PropTypes.oneOf(['sm', 'md']),
  heightStyle: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  showThumbnails: PropTypes.bool,
  thumbnailAlignment: PropTypes.oneOf(['left', 'right']),
  videoIconPlacement: PropTypes.oneOf(['image-center', 'image-bottom-left', 'image-bottom-right', 'image-top-left', 'image-top-right', 'inline-left', 'inline-right']),
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  showDescriptions: PropTypes.bool,
  showPlayButtonForVideo: PropTypes.bool,
  showTimestamp: PropTypes.bool,
  showUpdatedDate: PropTypes.bool,
  showBylines: PropTypes.bool,
  timestampOptions: PropTypes.shape({
    showElapsedTime: PropTypes.bool,
    displayShortDateTime: PropTypes.bool
  }),
  showNumbers: PropTypes.bool,
  maintainImageAspectRatio: PropTypes.bool,
  imageAspectRatioBackgroundColor: PropTypes.string,
  edgeToEdge: PropTypes.bool,
  enableDivider: PropTypes.bool,
  dividerOptions: PropTypes.shape({
    height: PropTypes.oneOf(Object.keys(INSET_STYLE)),
    backgroundColor: PropTypes.string,
    position: PropTypes.shape({
      aboveList: PropTypes.bool,
      belowList: PropTypes.bool
    })
  }),
  categoryUrl: PropTypes.string,
  showReadMore: PropTypes.bool,
  readMoreAlignment: PropTypes.oneOf(['left', 'right', 'center']),
  readMoreOptions: PropTypes.shape({
    text: PropTypes.string,
    fullWidth: PropTypes.bool,
    roundedCorner: PropTypes.oneOf(['slight', 'strong', 'no']),
    fontStyle: PropTypes.oneOf(['uppercase', 'regular']),
    backgroundColor: PropTypes.string,
    backgroundColorStyle: PropTypes.oneOf(Object.keys(COLOR_STYLE)),
    buttonStyle: PropTypes.oneOf(['solid', 'outline']),
    outlineColor: PropTypes.string,
    outlineColorStyle: PropTypes.oneOf(Object.keys(COLOR_STYLE)),
    urlTarget: PropTypes.string,
    textColor: PropTypes.string,
    textColorStyle: PropTypes.oneOf(Object.keys(COLOR_STYLE)),
    showMoreArrow: PropTypes.bool
  }),
  title: PropTypes.string,
  titleColor: PropTypes.string,
  titleType: PropTypes.string
};

const CardListItemDefaultProps = {
  widthStyle: 'sm',
  heightStyle: 'md',
  showThumbnails: true,
  thumbnailAlignment: 'right',
  videoIconPlacement: 'inline-right',
  backgroundColor: '#FFFFFF',
  textColor: '#000000',
  showDescriptions: false,
  showPlayButtonForVideo: true,
  showTimestamp: true,
  showUpdatedDate: false,
  showBylines: true,
  timestampOptions: {
    showElapsedTime: true,
    displayShortDateTime: true
  },
  showNumbers: false,
  maintainImageAspectRatio: false,
  edgeToEdge: false,
  enableDivider: false,
  dividerOptions: {
    height: 'xs'
  },
  showReadMore: false,
  readMoreAlignment: 'center'
};

class CardList extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    startArticleIndex: PropTypes.number,
    types: PropTypes.array,
    displaySizes: PropTypes.array,
    numberOfItems: PropTypes.number,
    ...CardListItemProps
  }

  static defaultProps = {
    startArticleIndex: 0,
    types: ['story', 'clip', 'link'],
    numberOfItems: 4,
    ...CardListItemDefaultProps
  }

  state = {
    listHeight: null
  }

  componentDidMount() {
    this._updateListHeight();
  }

  componentWillReceiveProps() {
    this._updateListHeight();
  }

  _updateListHeight() {
    const { enableDivider } = this.props;

    /**
     * Edge browser can't destructure cardlist DOM node.
     * Get firstListItem and itemContent not using destructuring.
     */
    const firstListItem = this.cardlist && this.cardlist.children && this.cardlist.children[0];
    const itemContent = firstListItem && firstListItem.children && firstListItem.children[0];

    if (enableDivider && itemContent) {
      const {
        dividerOptions: {
          height: dividerHeight,
          position: {
            belowList,
            aboveList
          } = {}
        } = {}
      } = this.props;

      const dividerHeightInPx = INSET_STYLE_VALUE_MAPPING[dividerHeight];

      /** The item height depends on both wrapper and the content of each item
       *  We take the higher value
      **/
      const currentItemHeight = Math.max(itemContent.offsetHeight, firstListItem.offsetHeight);
      const currentListHeight = this.cardlist.offsetHeight;

      const actualItemHeight = currentItemHeight + dividerHeightInPx;

      if (belowList && aboveList) {
        /** Dividers on both above and below */
        this._updateHeight(currentListHeight - dividerHeightInPx, actualItemHeight, dividerHeightInPx);
      } else if (!belowList && !aboveList) {
        /** Dividers between */
        this._updateHeight(currentListHeight + dividerHeightInPx, actualItemHeight, 0 - dividerHeightInPx);
      } else {
        this._updateHeight(currentListHeight, actualItemHeight);
      }
    }
  }

  _updateHeight(listHeightToDivide, actualItemHeight, adjustValue = 0) {
    if (this._itemNotFixHeight(listHeightToDivide, actualItemHeight)) {
      const numberOfItems = this._getNumberOfItems(listHeightToDivide, actualItemHeight);

      this.setState({
        listHeight: actualItemHeight * numberOfItems + adjustValue
      });
    }
  }

  _itemNotFixHeight(listHeight, itemHeight, allowedRemainder = 3) {
    /** Check if the itemHeight with the dividers will fix the list container
     *  The allowed remainder is for standard error when calculating offset height of items ie .33 value
     */
    return listHeight % itemHeight > allowedRemainder;
  }

  _getNumberOfItems(listHeight, itemHeight) {
    return Math.ceil(listHeight / itemHeight);
  }

  render() {
    const {
      FRN_rawResponses: [
        {
          data: {
            link,
            features: items = []
          } = {}
        } = {}
      ] = [],
      widthStyle,
      heightStyle,
      startArticleIndex,
      types,
      displaySizes = [],
      numberOfItems,
      showThumbnails,
      thumbnailAlignment,
      videoIconPlacement,
      showDescriptions,
      backgroundColor,
      textColor,
      showPlayButtonForVideo,
      showTimestamp,
      showUpdatedDate,
      showBylines,
      timestampOptions,
      showNumbers,
      maintainImageAspectRatio,
      imageAspectRatioBackgroundColor,
      edgeToEdge,
      enableDivider,
      dividerOptions = {},
      categoryUrl,
      showReadMore,
      readMoreAlignment: textAlign,
      readMoreOptions = {},
      title,
      titleType,
      titleColor
    } = this.props;

    const {
      backgroundColor: dividerBackgroundColor,
      height: dividerHeight,
      position: {
        belowList: dividerBelowList,
        aboveList: dividerAboveList
      } = {}
    } = dividerOptions;

    const {
      text = 'View More',
      roundedCorner = 'strong',
      buttonStyle = 'solid'
    } = readMoreOptions;

    const offset = Math.max(startArticleIndex, 0);
    const filteredItems = filterItems(items, { type: types, displaysize: displaySizes });
    const itemsToShow = filteredItems.slice(offset, numberOfItems + offset);
    const cardListClassName = classNames(
      'CardList',
      `CardList--${heightStyle}`,
      `CardList--${heightStyle}-${numberOfItems}`,
      {
        'CardList-edgeToEdge': edgeToEdge,
        'frn-u-divider': enableDivider,
        'CardList-divider--below-list': enableDivider && !dividerAboveList && dividerBelowList
      },
    );

    if (!itemsToShow.length) {
      return null;
    }

    const cardListStyle = {};

    if (enableDivider) {
      const { listHeight } = this.state;
      const dividerHeightInPx = INSET_STYLE_VALUE_MAPPING[dividerHeight];

      cardListStyle.height = listHeight;

      /** The divider will be the space between the items, so the container background color will be the divider color */
      cardListStyle.backgroundColor = dividerBackgroundColor;

      /**
       * In mobile screen size, adding padding bottom with negative margin for the last divider
       */
      if (dividerBelowList) {
        cardListStyle.paddingBottom = dividerHeightInPx;

        if (!dividerAboveList) {
          cardListStyle.marginBottom = 0 - dividerHeightInPx;
        }
      }
    }

    const content = itemsToShow.map((item, index) => {
      return (
      <CardListItem
        key={index}
        item={item}
        widthStyle={widthStyle}
        showThumbnails={showThumbnails}
        thumbnailAlignment={thumbnailAlignment}
        videoIconPlacement={videoIconPlacement}
        showDescriptions={showDescriptions}
        backgroundColor={backgroundColor}
        textColor={textColor}
        showPlayButtonForVideo={showPlayButtonForVideo}
        showTimestamp={showTimestamp}
        showUpdatedDate={showUpdatedDate}
        showBylines={showBylines}
        timestampOptions={timestampOptions}
        showNumbers={showNumbers}
        itemNumber={index + 1}
        maintainImageAspectRatio={maintainImageAspectRatio}
        imageAspectRatioBackgroundColor={imageAspectRatioBackgroundColor}
        enableDivider={enableDivider}
        dividerOptions={dividerOptions}
      />);
    });

    const readMoreUrl = categoryUrl || link;
    const readMoreStyle = {
      textAlign
    };

    return (
      <div>
        { title ? <ComponentTitle titleType={titleType} color={titleColor} showArrow={false} title={title}/> : null }
        <div className={cardListClassName} style={cardListStyle} ref={(cardlist) => this.cardlist = cardlist}>
          {content}
        </div>
        {showReadMore &&
          <div className="CardList-readMore" style={readMoreStyle}>
            <ReadMoreButton
              text={text}
              link={readMoreUrl}
              roundedCorner={roundedCorner}
              buttonStyle={buttonStyle}
              {...readMoreOptions}
            />
          </div>
        }
      </div>
    );
  }
}

class CardListItem extends Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    itemNumber: PropTypes.number,
    ...CardListItemProps
  }

  static defaultProps = {
    itemNumber: 1,
    ...CardListItemDefaultProps
  }

  render() {
    const {
      item,
      widthStyle,
      itemNumber,
      showThumbnails,
      thumbnailAlignment,
      videoIconPlacement,
      backgroundColor,
      textColor,
      showDescriptions,
      showPlayButtonForVideo,
      showTimestamp,
      showUpdatedDate,
      showBylines,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      },
      showNumbers,
      maintainImageAspectRatio,
      imageAspectRatioBackgroundColor,
      enableDivider,
      dividerOptions: {
        height: dividerHeight = 'xs',
        position: {
          aboveList: dividerAboveList,
          belowList: dividerBelowList
        } = {}
      }
    } = this.props;

    const {
      id = '',
      link = '',
      url = '',
      layout = '',
      headline = '',
      updatedDate = '',
      publishedDate = '',
      type: newsType = '',
      abstract: description = '',
      bylines = [],
      images = {},
      window: {
        height = '500',
        width = '500',
        showtoolbar = true,
        isnewwindow = false,
        showscrollbar = true,
        showlocation = true,
        isresizable = true
      } = {},
      seo: { pageurl: slug = ''} = {},
      surfaceable: [ { type: featureType = '' } = {} ] = [],
      images: {
        abstract: {
          filename: linkImage = ''
        } = {}
      } = {},

      abstractimage: {
        filename: newsImage = '',
        lastEditedDate = ''
      } = {},
      options: {
        date: {
          published: {
            showonabstract: showPublished = true
          } = {},
          updated: {
            showonabstract: showUpdated = true
          } = {}
        } = {}
      } = {}
    } = item;

    let locationUrl = '';

    try {
      if (typeof location === 'undefined') {
        const {hostname, port, protocol} = this.props.state.page;
        const location = {hostname, port, protocol: protocol.endsWith(':') ? protocol : protocol + ':'};
        locationUrl = getLocalLink(location, {id, type: newsType, slug, link});
      } else {
        locationUrl = getLocalLink(location, {id, type: newsType, slug, link});
      }
    } catch (err) {
      console.log('>>> getLocalLink error', err);
    }

    const showVideoIcon = showPlayButtonForVideo && (newsType.toLowerCase() === 'clip' || featureType.toLowerCase() === 'clip');
    const videoIconPosition = videoIconPlacement.slice(videoIconPlacement.indexOf('-') + 1, videoIconPlacement.length);

    const content = [];
    const showTimestampComponent = showBylines || showTimestamp;

    const contentClassNames = classNames(
      'CardList-item-content',
      {'CardList-item-content--noDescription': !showDescriptions},
      {'CardList-item-content--noTimestamp': !showTimestampComponent}
    );

    const titleClassNames = classNames(
      'CardList-item-title',
      {'CardList-item-title--noDescription': !showDescriptions || !description},
      {'CardList-item-title--noTimestamp': !showTimestampComponent},
      {[`TilteVideoThumbnail TilteVideoThumbnail-${videoIconPosition}`]: showVideoIcon && videoIconPlacement.includes('inline')}
    );

    const descriptionClassNames = classNames(
      'CardList-item-description',
      {'CardList-item-description--noTimestamp': !showTimestampComponent}
    );

    const textContainerClassNames = classNames(
      'CardList-item-textContainer',
      {'CardList-item-textContainer--withNumber': showNumbers},
      {[`CardList-item-textContainer--withNumber-${widthStyle}`]: showNumbers}
    );

    const iframeImage = _.get(images, 'graphic.filename', '');
    const isShowWindowLayout = isnewwindow && (newsType === 'link');
    const urlIFrameOrUrlLoc = layout === 3 ? (url || iframeImage) : locationUrl;

    const dataOfWindowOpen = getDataOfWindowOpen(width, height, showtoolbar, showscrollbar, showlocation, isresizable);

    let timestamp;

    if (showTimestampComponent) {
      let timestampDate;

      if (showTimestamp) {
        const publishedDateTimestamp = showPublished ? publishedDate : null;
        const updatedDateTimestamp = showUpdated ? updatedDate : null;

        timestampDate = showUpdatedDate ? updatedDateTimestamp : publishedDateTimestamp;
      }

      timestamp = <Timestamp publishDate={timestampDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime} authors={showBylines ? _.map(bylines, bylineToString) : null} />;
    }

    content.push(
      <div key={content.length}
        className={textContainerClassNames}>
        <div className={contentClassNames} style={{color: textColor}}>
        {isShowWindowLayout ?
        <a className='CardList-hoverCardList' onClick={() => showWindow(urlIFrameOrUrlLoc, '_blank', dataOfWindowOpen)}>
          <div className={titleClassNames} style={{color: textColor}}>{headline}</div>
        </a> :
        <a href={urlIFrameOrUrlLoc}>
          <div className={titleClassNames} style={{color: textColor}}>{headline}</div>
        </a>}
          {showDescriptions && <p className={descriptionClassNames} dangerouslySetInnerHTML={{ __html: description.replace(/p>/g, 'span>') }}></p>}
        </div>
        {timestamp}
      </div>
    );

    if (showNumbers) {
      content.unshift(
        <div key={content.length} className="CardList-item-number">{itemNumber}.</div>
      );
    }

    if (showThumbnails) {
      const imageContainerStyles = {};

      imageContainerStyles['backgroundColor'] = backgroundColor;

      let imageStyles;
      if (maintainImageAspectRatio) {
        imageContainerStyles['backgroundColor'] = imageAspectRatioBackgroundColor;
        imageStyles = {
          objectFit: 'contain'
        };
      }

      const imageContainerClassNames = classNames(
        `CardList-item-imageContainer`,
        {'CardList-hoverCardList': isShowWindowLayout},
        `CardList-item-imageContainer--${widthStyle}`,
        {[`VideoThumbnail VideoThumbnail-${videoIconPosition}`]: showVideoIcon && videoIconPlacement.includes('image')}
      );

      const imgClassNames = classNames(
        'CardList-item-imageContainer-image',
        {[`VideoThumbnail VideoThumbnail-${videoIconPosition}`]: showVideoIcon && videoIconPlacement.includes('image')},
        {'CardList-item-imageContainer-image--scale': maintainImageAspectRatio}
      );

      const isOnClick = () => {isShowWindowLayout && showWindow(urlIFrameOrUrlLoc, '_blank', dataOfWindowOpen);};

      const thumbnailContent = (
        <a onClick={isOnClick} key={content.length}
          href={isShowWindowLayout ? null : urlIFrameOrUrlLoc}
          className={imageContainerClassNames}
          style={imageContainerStyles}>
          <span className={imgClassNames} style={{backgroundColor: imageAspectRatioBackgroundColor}}>
            <Image
              imageClassNames="img-responsive"
              src={ newsType === 'link' ? linkImage : newsImage }
              lastEditedDate={lastEditedDate}
              alt={headline}
              imageStyles={imageStyles}
              imageBreakpoints={IMAGE_BREAKPOINTS}
            />
          </span>
        </a>
      );

      if (thumbnailAlignment === 'left') {
        content.unshift(thumbnailContent);
      } else {
        content.push(thumbnailContent);
      }
    }

    const itemStyles = {
      color: textColor,
      backgroundColor
    };

    const itemClassnames = classNames(
      'CardList-item',
      {
        'CardList-item-margin--default': !enableDivider,
        'CardList-item-margin--divider': enableDivider,
        [`frn-u-margin-${dividerHeight}`]: enableDivider,
        'CardList-item-divider--above-list': enableDivider && dividerAboveList,
        'CardList-item-divider--between': enableDivider && !dividerAboveList && !dividerBelowList,
        'CardList-item-divider--below-list': enableDivider && dividerBelowList,
      }
    );

    /**
     * Override frn-u-margin-* based on divider position
     */
    if (enableDivider) {
      if (dividerAboveList) {
        itemStyles['marginBottom'] = 0;
      } else {
        itemStyles['marginTop'] = 0;
      }
    }

    return (
      <div style={itemStyles} className={itemClassnames}>
        <div className="CardList-item-container">
          {content}
        </div>
      </div>
    );
  }
}

CardListItem = withResources(CardListItem);
export default CardList;



// WEBPACK FOOTER //
// ./app/components/news/CardList.js